import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, 
  ChevronRight, 
  ChevronDown, 
  BookOpen, 
  Copy, 
  Check,
  ArrowLeft,
  Globe,
  Settings,
  Zap,
  Shield,
  Code,
  MapPin,
  Building,
  Map,
  Hash,
  Crosshair,
  Network,
  Lock,
  Menu,
  X
} from 'lucide-react';
import { documentationData } from '../mock/documentationData';
import { useTranslation } from '../hooks/useTranslation';
import { useSEO } from '../hooks/useSEO';

const DocumentationPage = () => {
  useSEO('docs');
  const { section, subsection, subsubsection } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    'residential-proxies': true, // Expand residential-proxies by default
    'location-settings': true // Expand location-settings by default
  });
  const [copiedCode, setCopiedCode] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);

  // Get current content based on URL params
  const getCurrentContent = () => {
    if (!section) {
      return documentationData.sections.find(s => s.slug === 'residential-proxies');
    }
    
    const mainSection = documentationData.sections.find(s => s.slug === section);
    if (!mainSection) return null;
    
    if (!subsection) {
      return mainSection;
    }
    
    // Look for subsection in nested content
    const findSubsection = (items) => {
      for (const item of items) {
        if (item.slug === subsection) {
          return item;
        }
        if (item.children) {
          const found = findSubsection(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    const subsectionContent = findSubsection(mainSection.children || []);
    
    // If we have a third level (subsubsection), look for it
    if (subsubsection && subsectionContent && subsectionContent.children) {
      const subsubsectionContent = subsectionContent.children.find(item => item.slug === subsubsection);
      return subsubsectionContent || subsectionContent;
    }
    
    return subsectionContent;
  };

  const currentContent = getCurrentContent();

  const toggleSection = (sectionSlug) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionSlug]: !prev[sectionSlug]
    }));
  };

  // Build URL for a documentation item
  const buildItemUrl = (item, parentSlug = '', grandParentSlug = '') => {
    if (!parentSlug) {
      return `/docs/${item.slug}`;
    } else if (!grandParentSlug) {
      return `/docs/${parentSlug}/${item.slug}`;
    } else {
      return `/docs/${grandParentSlug}/${parentSlug}/${item.slug}`;
    }
  };

  // Search through content blocks
  const searchContentBlocks = (content, query) => {
    if (!content || !query || query.trim() === '') return false;
    
    const searchLower = query.toLowerCase().trim();
    
    for (const block of content) {
      if (block.type === 'paragraph' && block.text && block.text.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (block.type === 'heading' && block.text && block.text.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (block.type === 'list' && block.items) {
        for (const item of block.items) {
          if (typeof item === 'string' && item.toLowerCase().includes(searchLower)) {
            return true;
          }
        }
      }
      if (block.type === 'code' && block.code && block.code.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (block.type === 'note' && block.text && block.text.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (block.type === 'note' && block.title && block.title.toLowerCase().includes(searchLower)) {
        return true;
      }
      if (block.type === 'table' && block.rows) {
        for (const row of block.rows) {
          for (const cell of row) {
            if (typeof cell === 'string' && cell.toLowerCase().includes(searchLower)) {
              return true;
            }
          }
        }
      }
    }
    
    return false;
  };

  // Get preview text from content
  const getContentPreview = (content, query, maxLength = 100) => {
    if (!content || !query) return '';
    
    const searchLower = query.toLowerCase().trim();
    
    for (const block of content) {
      if (block.type === 'paragraph' && block.text) {
        const index = block.text.toLowerCase().indexOf(searchLower);
        if (index !== -1) {
          const start = Math.max(0, index - 30);
          const end = Math.min(block.text.length, index + searchLower.length + 50);
          let preview = block.text.substring(start, end);
          if (start > 0) preview = '...' + preview;
          if (end < block.text.length) preview = preview + '...';
          return preview;
        }
      }
      if (block.type === 'heading' && block.text && block.text.toLowerCase().includes(searchLower)) {
        return block.text;
      }
      if (block.type === 'list' && block.items) {
        for (const item of block.items) {
          if (typeof item === 'string' && item.toLowerCase().includes(searchLower)) {
            return item;
          }
        }
      }
    }
    
    return '';
  };

  // Search through all documentation content
  const performContentSearch = (query) => {
    if (!query || query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const results = [];
    const hiddenSlugs = ['continent', 'zip-postal-code', 'coordinates', 'asn-targeting'];
    const searchLower = query.toLowerCase().trim();

    const searchInItem = (item, parentSlug = '', grandParentSlug = '') => {
      if (hiddenSlugs.includes(item.slug)) return;

      // Check title and description
      const title = getTranslatedTitle(item).toLowerCase();
      const description = getTranslatedDescription(item).toLowerCase();
      const slug = item.slug.toLowerCase();
      
      let matches = false;
      let preview = '';

      if (title.includes(searchLower) || description.includes(searchLower) || slug.includes(searchLower)) {
        matches = true;
        preview = description || title;
      }

      // Check content blocks
      if (item.content && searchContentBlocks(item.content, query)) {
        matches = true;
        preview = getContentPreview(item.content, query) || preview || description;
      }

      if (matches) {
        results.push({
          item,
          url: buildItemUrl(item, parentSlug, grandParentSlug),
          preview: preview || description || title,
          title: getTranslatedTitle(item)
        });
      }

      // Search in children
      if (item.children) {
        item.children.forEach(child => {
          searchInItem(child, item.slug, parentSlug);
        });
      }
    };

    documentationData.sections.forEach(section => {
      searchInItem(section);
    });

    setSearchResults(results.slice(0, 10)); // Limit to 10 results
  };

  // Check if an item matches the search query
  const itemMatchesSearch = (item, query) => {
    if (!query || query.trim() === '') return true;
    
    const searchLower = query.toLowerCase().trim();
    const title = getTranslatedTitle(item).toLowerCase();
    const description = getTranslatedDescription(item).toLowerCase();
    const slug = item.slug.toLowerCase();
    
    return title.includes(searchLower) || 
           description.includes(searchLower) || 
           slug.includes(searchLower);
  };

  // Check if an item or any of its children match the search
  const itemOrChildrenMatchSearch = (item, query) => {
    if (!query || query.trim() === '') return true;
    
    if (itemMatchesSearch(item, query)) return true;
    
    if (item.children) {
      return item.children.some(child => itemOrChildrenMatchSearch(child, query));
    }
    
    return false;
  };

  // Perform content search when query changes
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '') {
      performContentSearch(searchQuery);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  }, [searchQuery]);

  // Update dropdown visibility when results change
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '' && searchResults.length > 0) {
      setShowSearchDropdown(true);
    } else if (searchQuery && searchQuery.trim() !== '' && searchResults.length === 0) {
      setShowSearchDropdown(true); // Show "no results" message
    }
  }, [searchResults, searchQuery]);

  // Auto-expand sections when searching
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '') {
      setExpandedSections(prev => {
        const newExpanded = { ...prev };
        
        const expandMatchingSections = (items) => {
          items.forEach(item => {
            // Hide specific items from the menu
            const hiddenSlugs = ['continent', 'zip-postal-code', 'coordinates', 'asn-targeting'];
            if (hiddenSlugs.includes(item.slug)) return;
            
            if (item.children) {
              const visibleChildren = item.children.filter(child => !hiddenSlugs.includes(child.slug));
              if (visibleChildren.some(child => itemOrChildrenMatchSearch(child, searchQuery))) {
                newExpanded[item.slug] = true;
              }
              expandMatchingSections(item.children);
            }
          });
        };
        
        expandMatchingSections(documentationData.sections);
        return newExpanded;
      });
    } else {
      // Reset to default expanded sections when search is cleared
      setExpandedSections({
        'residential-proxies': true,
        'location-settings': true
      });
    }
  }, [searchQuery]);

  const copyToClipboard = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(''), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  // Helper function to get translated title for sidebar items
  const getTranslatedTitle = (item) => {
    const key = item.slug.replace(/-/g, '');
    
    // Map to specific translation keys
    const keyMappings = {
      'residentialproxies': 'documentation.sections.residentialProxies.title',
      'gettingstarted': 'documentation.sections.gettingStarted.title',
      'makingrequests': 'documentation.sections.makingRequests.title',
      'locationsettings': 'documentation.sections.locationSettings.title',
      'country': 'documentation.sections.country.title',
      'city': 'documentation.sections.city.title',
      'state': 'documentation.sections.state.title',
      'continent': 'documentation.sections.continent.title',
      'zippostalcode': 'documentation.sections.zipPostalCode.title',
      'coordinates': 'documentation.sections.coordinates.title',
      'asntargeting': 'documentation.sections.asnTargeting.title',
      'sessioncontrol': 'documentation.sections.sessionControl.title',
      'protocols': 'documentation.sections.protocols.title',
      'restrictedtargets': 'documentation.sections.restrictedTargets.title'
    };
    
    const translationKey = keyMappings[key];
    if (translationKey) {
      const translated = t(translationKey);
      if (translated !== translationKey) {
        return translated;
      }
    }
    
    return item.title; // fallback to original title
  };

  // Helper function to get translated description
  const getTranslatedDescription = (item) => {
    const key = item.slug.replace(/-/g, '');
    
    // Map to specific translation keys
    const keyMappings = {
      'residentialproxies': 'documentation.sections.residentialProxies.description',
      'gettingstarted': 'documentation.sections.gettingStarted.description',
      'makingrequests': 'documentation.sections.makingRequests.description',
      'locationsettings': 'documentation.sections.locationSettings.description',
      'country': 'documentation.sections.country.description',
      'city': 'documentation.sections.city.description',
      'state': 'documentation.sections.state.description',
      'continent': 'documentation.sections.continent.description',
      'zippostalcode': 'documentation.sections.zipPostalCode.description',
      'coordinates': 'documentation.sections.coordinates.description',
      'asntargeting': 'documentation.sections.asnTargeting.description',
      'sessioncontrol': 'documentation.sections.sessionControl.description',
      'protocols': 'documentation.sections.protocols.description',
      'restrictedtargets': 'documentation.sections.restrictedTargets.description'
    };
    
    const translationKey = keyMappings[key];
    if (translationKey) {
      const translated = t(translationKey);
      if (translated !== translationKey) {
        return translated;
      }
    }
    
    return item.description; // fallback to original description
  };

  // Helper function to translate content blocks
  const getTranslatedContent = (block, contentSlug) => {
    if (contentSlug === 'residential-proxies') {
      const translations = t('documentation.sections.residentialProxies.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('IP9 Residential Proxies contain real IP addresses')) {
          return translations.introText1 || block.text;
        }
        if (block.text.includes('IP9 Residential Proxies are fully equipped')) {
          return translations.introText2 || block.text;
        }
        if (block.text.includes('Get started with residential proxies')) {
          return translations.quickStartText || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Key Features') {
          return translations.headings?.keyFeatures || block.text;
        }
        if (block.text === 'Quick Start') {
          return translations.headings?.quickStart || block.text;
        }
      }
      
      if (block.type === 'list' && block.items) {
        return translations.features || block.items;
      }
      
      if (block.type === 'note') {
        if (block.title === 'Authentication Required') {
          return {
            title: translations.notes?.authenticationRequired || block.title,
            text: translations.notes?.authenticationNote || block.text
          };
        }
      }
    }
    
    if (contentSlug === 'getting-started') {
      const translations = t('documentation.sections.gettingStarted.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('Make a request to https://httpbin.org/ip using a residential proxy')) {
          return translations.introText1 || block.text;
        }
        if (block.text.includes('Use httpbin.org/ip to check the parameters')) {
          return translations.introText2 || block.text;
        }
        if (block.text.includes('Our Residential Proxies support HTTP, HTTPS, and SOCKS5')) {
          return translations.basicSetupIntro || block.text;
        }
        if (block.text.includes('Now make a request using a proxy from France')) {
          return translations.countryTargetingIntro || block.text;
        }
        if (block.text.includes('Here is an example of a request that goes through a proxy from London')) {
          return translations.cityTargetingIntro || block.text;
        }
        if (block.text.includes('If you need to keep the same proxy for more than one request')) {
          return translations.sessionControlIntro || block.text;
        }
        if (block.text.includes('If you have any additional questions')) {
          return translations.supportContact || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Basic Setup') {
          return translations.headings?.basicSetup || block.text;
        }
        if (block.text === 'Country Targeting') {
          return translations.headings?.countryTargeting || block.text;
        }
        if (block.text === 'City-Level Targeting') {
          return translations.headings?.cityLevelTargeting || block.text;
        }
        if (block.text === 'Session Control') {
          return translations.headings?.sessionControl || block.text;
        }
      }
      
      if (block.type === 'note') {
        if (block.title === 'Dashboard Access') {
          return {
            title: translations.notes?.dashboardAccess || block.title,
            text: translations.notes?.dashboardNote || block.text
          };
        }
      }
    }
    
    if (contentSlug === 'making-requests') {
      const translations = t('documentation.sections.makingRequests.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('Basic query only requires passing username and password')) {
          return translations.basicQueryIntro || block.text;
        }
        if (block.text.includes('In this example, a query to httpbin.org/ip is performed')) {
          return translations.codeExampleIntro || block.text;
        }
        if (block.text.includes('A single backconnect proxy (residential.ip9.io:8000) enables you')) {
          return translations.advancedConfigIntro || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Basic Query') {
          return translations.headings?.basicQuery || block.text;
        }
        if (block.text === 'Code Examples') {
          return translations.headings?.codeExamples || block.text;
        }
        if (block.text === 'Advanced Configuration') {
          return translations.headings?.advancedConfiguration || block.text;
        }
        if (block.text === 'Query Parameters') {
          return translations.headings?.queryParameters || block.text;
        }
      }
      
      if (block.type === 'table') {
        return {
          headers: translations.tableHeaders || block.headers,
          rows: block.rows.map(row => {
            const paramKey = row[0].toLowerCase();
            return [
              row[0], // Keep parameter name in English
              translations.parameterDescriptions?.[paramKey] || row[1]
            ];
          })
        };
      }
    }
    
    if (contentSlug === 'location-settings') {
      const translations = t('documentation.sections.locationSettings.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('IP9 Residential proxy network covers 200+ locations')) {
          return translations.introText1 || block.text;
        }
        if (block.text.includes('You can find more information on selecting geo-location parameters')) {
          return translations.introText2 || block.text;
        }
        if (block.text.includes('We use the MaxMind GeoIP2 database')) {
          return translations.mappingIntro || block.text;
        }
        if (block.text.includes('Some other public IP databases may show different locations')) {
          return translations.mappingNote || block.text;
        }
        if (block.text.includes('If you have any additional questions, please contact your account manager')) {
          return translations.supportContact || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Country, State, City Mapping') {
          return translations.headings?.countryStateCityMapping || block.text;
        }
      }
      
      if (block.type === 'list' && block.items) {
        return translations.listItems || block.items;
      }
    }
    
    if (contentSlug === 'country') {
      const translations = t('documentation.sections.country.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('Adding a country flag to the authorization header')) {
          return translations.introText || block.text;
        }
        if (block.text.includes('Here are a few examples of country parameters')) {
          return translations.exampleIntro || block.text;
        }
        if (block.text.includes('In this example, a query to httpbin.org/ip is performed from a random IP address from Germany')) {
          return translations.codeExampleIntro || block.text;
        }
        if (block.text.includes('IP9 residential network has coverage in almost every country in the world')) {
          return translations.nodeDescription || block.text;
        }
        if (block.text.includes('You do not need to pass any additional parameters, just username:password')) {
          return translations.authDescription || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Code Example') {
          return translations.headings?.codeExample || block.text;
        }
        if (block.text === 'Country Specific Entry Nodes') {
          return translations.countrySpecificNodes || block.text;
        }
      }
      
      if (block.type === 'note') {
        if (block.title === 'Entry Points') {
          return {
            title: block.title,
            text: translations.entryPointsNote || block.text
          };
        }
      }
      
      if (block.type === 'table') {
        return {
          headers: translations.tableHeaders || block.headers,
          rows: block.rows.map(row => {
            const countryName = row[0];
            return [
              translations.countryNames?.[countryName] || countryName,
              row[1] // Keep parameter unchanged
            ];
          })
        };
      }
    }
    
    if (contentSlug === 'city') {
      const translations = t('documentation.sections.city.content', { returnObjects: true });
      
      if (block.type === 'paragraph') {
        if (block.text.includes('Add a city parameter to narrow down to city-level targeting')) {
          return translations.introText || block.text;
        }
        if (block.text.includes('We support every city in the world')) {
          return translations.coverageText || block.text;
        }
        if (block.text.includes('Here are a few examples of valid combinations')) {
          return translations.exampleIntro || block.text;
        }
        if (block.text.includes('In this example, a query to httpbin.org/ip is performed from a random IP address from Munich')) {
          return translations.codeExampleIntro || block.text;
        }
      }
      
      if (block.type === 'heading') {
        if (block.text === 'Code Examples') {
          return translations.headings?.codeExamples || block.text;
        }
      }
      
      if (block.type === 'list' && block.items) {
        return translations.cityExamples || block.items;
      }
      
      if (block.type === 'note') {
        if (block.title === 'City List') {
          return {
            title: translations.cityListTitle || block.title,
            text: translations.cityListNote || block.text
          };
        }
      }
    }
    
    // Handle all remaining pages
    const pageTranslations = {
      'state': 'documentation.sections.state.content',
      'continent': 'documentation.sections.continent.content',
      'zip-postal-code': 'documentation.sections.zipPostalCode.content',
      'coordinates': 'documentation.sections.coordinates.content',
      'asn-targeting': 'documentation.sections.asnTargeting.content',
      'session-control': 'documentation.sections.sessionControl.content',
      'protocols': 'documentation.sections.protocols.content',
      'restricted-targets': 'documentation.sections.restrictedTargets.content'
    };
    
    if (pageTranslations[contentSlug]) {
      const translations = t(pageTranslations[contentSlug], { returnObjects: true });
      
      if (block.type === 'paragraph') {
        // State page
        if (contentSlug === 'state') {
          if (block.text.includes('To get a proxy from a specific US state')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('Here are a few examples of valid combinations of state parameters')) {
            return translations.exampleIntro || block.text;
          }
          if (block.text.includes('In this example, a query to httpbin.org/ip is performed from a random IP address from California')) {
            return translations.stateCodeExampleIntro || block.text;
          }
          if (block.text.includes('Contact our support team at support@ip9.io for a complete list of supported US states')) {
            return translations.stateListNote || block.text;
          }
        }
        
        // Continent page
        if (contentSlug === 'continent') {
          if (block.text.includes('Continental targeting allows you to route requests')) {
            return translations.introText || block.text;
          }
        }
        
        // ZIP/Postal Code page
        if (contentSlug === 'zip-postal-code') {
          if (block.text.includes('ZIP/Postal code targeting provides the most precise')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('This targeting method is available for supported countries')) {
            return translations.precisionText || block.text;
          }
        }
        
        // Coordinates page
        if (contentSlug === 'coordinates') {
          if (block.text.includes('Coordinate targeting allows you to specify exact latitude')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('This advanced targeting method is useful')) {
            return translations.advancedText || block.text;
          }
          if (block.text.includes('You can use either Country or ASN parameter per each request')) {
            return translations.precisionNote || block.text;
          }
        }
        
        // ASN Targeting page
        if (contentSlug === 'asn-targeting') {
          if (block.text.includes('Our Residential Proxies support targeting by ASN number')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('Below is the example for T-Mobile')) {
            return translations.exampleIntro || block.text;
          }
          if (block.text.includes('You can find ASN numbers using online ASN lookup tools')) {
            return translations.lookupText || block.text;
          }
        }
        
        // Session Control page
        if (contentSlug === 'session-control') {
          if (block.text.includes('The session ID parameter allows you to keep the same IP address')) {
            return translations.sessionIntro || block.text;
          }
          if (block.text.includes('The standard session time is 10 minutes')) {
            return translations.sessionDuration || block.text;
          }
          if (block.text.includes('Using sesstime parameter')) {
            return translations.extendedIntro || block.text;
          }
          if (block.text.includes('You can use the sesstime parameter to maintain the same IP for up to 1440 minutes')) {
            return translations.sessionTimeText || block.text;
          }
        }
        
        // Protocols page
        if (contentSlug === 'protocols') {
          if (block.text.includes('IP9 residential proxies support HTTP protocol')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('Our documentation includes code examples showcasing how to connect to the proxies using HTTP protocol')) {
            return translations.httpIntro || block.text;
          }
          if (block.text.includes('The default configuration of proxy ports includes 80 and 443')) {
            return translations.portsIntro || block.text;
          }
        }
        
        // Restricted Targets page
        if (contentSlug === 'restricted-targets') {
          if (block.text.includes('Some websites are restricted on our IP9 Residential Proxy network')) {
            return translations.introText || block.text;
          }
          if (block.text.includes('Some targets can be unblocked after going through a short KYC procedure')) {
            return translations.kycText || block.text;
          }
          if (block.text.includes('Pay-as-you-go')) {
            return translations.payAsYouGoText || block.text;
          }
          if (block.text.includes('Get in touch with our customer support team')) {
            return translations.contactText || block.text;
          }
          if (block.text.includes('To check if your target is blocked')) {
            return translations.ipCheckingText || block.text;
          }
        }
      }
      
      if (block.type === 'heading') {
        // Continent page headings
        if (contentSlug === 'continent' && block.text === 'Supported Continents') {
          return translations.headings?.supportedContinents || block.text;
        }
        
        // State page headings  
        if (contentSlug === 'state') {
          if (block.text === 'Code Example') {
            return translations.headings?.codeExample || block.text;
          }
        }
        
        // Session Control page headings
        if (contentSlug === 'session-control') {
          if (block.text === 'Establishing Session') {
            return translations.headings?.establishingSession || block.text;
          }
          if (block.text === 'Extended Session') {
            return translations.headings?.extendedSession || block.text;
          }
          if (block.text === 'Session Time') {
            return translations.headings?.sessionTime || block.text;
          }
        }
        
        // Protocols page headings
        if (contentSlug === 'protocols') {
          if (block.text === 'HTTP') {
            return translations.headings?.http || block.text;
          }
          if (block.text === 'HTTPS') {
            return translations.headings?.https || block.text;
          }
          if (block.text === 'SOCKS5') {
            return translations.headings?.socks5 || block.text;
          }
          if (block.text === 'Ports of Proxies') {
            return translations.headings?.portsOfProxies || block.text;
          }
        }
        
        // Restricted Targets page headings
        if (contentSlug === 'restricted-targets') {
          if (block.text === 'Restricted Target Categories') {
            return translations.headings?.restrictedTargetCategories || block.text;
          }
          if (block.text === 'Important Notice') {
            return translations.headings?.importantNotice || block.text;
          }
        }
      }
      
      if (block.type === 'list' && block.items) {
        if (contentSlug === 'state') {
          return translations.stateExamples || block.items;
        }
        if (contentSlug === 'continent') {
          return translations.continents || block.items;
        }
        if (contentSlug === 'restricted-targets') {
          return translations.categories || block.items;
        }
      }
      
      if (block.type === 'note') {
        if (contentSlug === 'state' && block.title === 'US States List') {
          return {
            title: translations.stateListTitle || block.title,
            text: translations.stateListNote || block.text
          };
        }
        if (contentSlug === 'zip-postal-code' && block.title === 'Availability') {
          return {
            title: translations.availabilityTitle || block.title,
            text: translations.availabilityNote || block.text
          };
        }
        if (contentSlug === 'coordinates' && block.title === 'Precision') {
          return {
            title: translations.precisionTitle || block.title,
            text: translations.coordinateNote || block.text
          };
        }
        if (contentSlug === 'asn-targeting' && block.title === 'Important') {
          return {
            title: translations.importantTitle || block.title,
            text: translations.importantNote || block.text
          };
        }
      }
      
      if (block.type === 'table') {
        if (contentSlug === 'protocols') {
          return {
            headers: translations.tableHeaders || block.headers,
            rows: block.rows
          };
        }
        if (contentSlug === 'restricted-targets') {
          return {
            headers: translations.tableHeaders || block.headers,
            rows: translations.categories || block.rows
          };
        }
      }
      
      if (block.type === 'note') {
        if (contentSlug === 'protocols' && block.title === 'Note') {
          return {
            title: translations.notes?.title || block.title,
            text: translations.notes?.socks5Warning || block.text
          };
        }
        if (contentSlug === 'restricted-targets' && block.title === 'Important') {
          return {
            title: translations.importantNote || block.title,
            text: translations.listNote || block.text
          };
        }
        if (contentSlug === 'restricted-targets' && block.title === 'IP Checking') {
          return {
            title: translations.ipCheckingNote || block.title,
            text: translations.ipCheckingText || block.text
          };
        }
        if (contentSlug === 'coordinates') {
          return {
            title: block.title,
            text: translations.coordinateNote || block.text
          };
        }
      }
    }
    
    return block.type === 'note' ? { title: block.title, text: block.text } : block.text;
  };

  const renderSidebarItem = (item, level = 0, parentSlug = '') => {
    // Hide specific items from the menu
    const hiddenSlugs = ['continent', 'zip-postal-code', 'coordinates', 'asn-targeting'];
    if (hiddenSlugs.includes(item.slug)) {
      return null;
    }

    // Filter by search query
    const hasSearchQuery = searchQuery && searchQuery.trim() !== '';
    if (hasSearchQuery && !itemOrChildrenMatchSearch(item, searchQuery)) {
      return null;
    }

    const isExpanded = expandedSections[item.slug];
    // Filter children to exclude hidden items and search non-matches
    let visibleChildren = item.children ? item.children.filter(child => !hiddenSlugs.includes(child.slug)) : [];
    if (hasSearchQuery) {
      visibleChildren = visibleChildren.filter(child => itemOrChildrenMatchSearch(child, searchQuery));
    }
    const hasChildren = visibleChildren.length > 0;
    const isActive = (section === item.slug && !subsection) || 
                     subsection === item.slug || 
                     subsubsection === item.slug;

    // Construct proper URL based on level
    let itemUrl;
    if (level === 0) {
      itemUrl = `/docs/${item.slug}`;
    } else if (level === 1) {
      itemUrl = `/docs/residential-proxies/${item.slug}`;
    } else if (level === 2) {
      itemUrl = `/docs/residential-proxies/${parentSlug}/${item.slug}`;
    }

    return (
      <div key={item.slug} className={`${level > 0 ? 'ml-4' : ''}`}>
        <div className="flex items-center group">
          {hasChildren && (
            <button
              onClick={() => toggleSection(item.slug)}
              className="p-1 hover:bg-slate-800 rounded mr-1"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
            </button>
          )}
          
          <Link
            to={itemUrl}
            onClick={() => setIsMobileMenuOpen(false)} // Close mobile menu on navigation
            className={`flex-1 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
              isActive 
                ? 'bg-cyan-500/10 text-cyan-400 border-r-2 border-cyan-500' 
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            {item.icon && <item.icon className="w-4 h-4 mr-2" />}
            {getTranslatedTitle(item)}
          </Link>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {visibleChildren.map(child => renderSidebarItem(child, level + 1, item.slug))}
          </div>
        )}
      </div>
    );
  };

  const renderCodeBlock = (code, language, id) => (
    <div className="my-6 bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center space-x-3">
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          {language && (
            <span className="text-cyan-400 text-xs font-mono bg-slate-800 px-2 py-1 rounded">
              {language}
            </span>
          )}
        </div>
        
        <button
          onClick={() => copyToClipboard(code, id)}
          className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors duration-200 text-sm"
        >
          {copiedCode === id ? (
            <>
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      
      <div className="relative">
        <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
          <code className="text-slate-300 font-mono">
            {code}
          </code>
        </pre>
      </div>
    </div>
  );

  const renderContent = (content) => {
    if (!content) {
      return (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">{t('documentation.notFound.title')}</h2>
          <p className="text-slate-400 mb-6">{t('documentation.notFound.description')}</p>
          <Link to="/docs">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('documentation.notFound.backToDocumentation')}
            </Button>
          </Link>
        </div>
      );
    }

    return (
      <div className="prose prose-invert prose-slate max-w-none">
        <h1 className="text-4xl font-bold text-white mb-4">{getTranslatedTitle(content)}</h1>
        <p className="text-xl text-slate-400 mb-8">{getTranslatedDescription(content)}</p>
        
        <div className="space-y-8">
          {content.content?.map((block, index) => {
            switch (block.type) {
              case 'heading':
                const HeadingTag = `h${block.level}`;
                return (
                  <HeadingTag 
                    key={index} 
                    className={`font-bold text-white ${
                      block.level === 2 ? 'text-2xl mt-12 mb-4' : 
                      block.level === 3 ? 'text-xl mt-8 mb-3' : 
                      'text-lg mt-6 mb-2'
                    }`}
                  >
                    {getTranslatedContent(block, content.slug)}
                  </HeadingTag>
                );
              
              case 'paragraph':
                return (
                  <p key={index} className="text-slate-300 leading-relaxed mb-4">
                    {getTranslatedContent(block, content.slug)}
                  </p>
                );
              
              case 'code':
                return renderCodeBlock(block.code, block.language, `code-${index}`);
              
              case 'list':
                return (
                  <ul key={index} className="space-y-2 mb-6">
                    {(getTranslatedContent(block, content.slug) || block.items).map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start text-slate-300">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                );
              
              case 'note':
                const noteContent = getTranslatedContent(block, content.slug);
                return (
                  <div key={index} className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Shield className="w-5 h-5 text-cyan-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-cyan-100 font-medium mb-1">{noteContent.title}</p>
                        <p className="text-cyan-200 text-sm">{noteContent.text}</p>
                      </div>
                    </div>
                  </div>
                );
              
              case 'table':
                const tableContent = getTranslatedContent(block, content.slug);
                const headers = (tableContent && tableContent.headers) || block.headers;
                const rows = (tableContent && tableContent.rows) || block.rows;
                return (
                  <div key={index} className="overflow-x-auto mb-6">
                    <table className="w-full bg-slate-800/50 rounded-lg border border-slate-700">
                      <thead>
                        <tr className="border-b border-slate-700">
                          {headers.map((header, headerIndex) => (
                            <th 
                              key={headerIndex} 
                              className="px-4 py-3 text-left text-sm font-medium text-slate-300 bg-slate-800/80"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                            {row.map((cell, cellIndex) => (
                              <td 
                                key={cellIndex} 
                                className="px-4 py-3 text-sm text-slate-300"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              
              case 'warning':
                return (
                  <div key={index} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Zap className="w-5 h-5 text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-100 font-medium mb-1">{block.title}</p>
                        <p className="text-yellow-200 text-sm">{block.text}</p>
                      </div>
                    </div>
                  </div>
                );
              
              default:
                return null;
            }
          })}
        </div>
        
        {/* Navigation */}
        {content.navigation && (
          <div className="flex justify-between items-center mt-16 pt-8 border-t border-slate-800">
            {content.navigation.prev ? (
              <Link 
                to={content.navigation.prev.url}
                className="flex items-center text-slate-400 hover:text-cyan-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <div>
                  <div className="text-sm text-slate-500">Previous</div>
                  <div className="font-medium">{content.navigation.prev.title}</div>
                </div>
              </Link>
            ) : <div></div>}
            
            {content.navigation.next && (
              <Link 
                to={content.navigation.next.url}
                className="flex items-center text-slate-400 hover:text-cyan-400 transition-colors text-right"
              >
                <div>
                  <div className="text-sm text-slate-500">Next</div>
                  <div className="font-medium">{content.navigation.next.title}</div>
                </div>
                <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      {/* Mobile menu toggle */}
      <div className="md:hidden bg-slate-900/50 border-b border-slate-800 px-4 py-3">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center text-slate-300 hover:text-white transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5 mr-2" />
          ) : (
            <Menu className="w-5 h-5 mr-2" />
          )}
          {t('documentation.menuToggle')}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className={`
          w-80 bg-slate-900/50 border-r border-slate-800 flex flex-col
          md:relative md:translate-x-0 md:z-0
          ${isMobileMenuOpen ? 'fixed inset-y-0 left-0 z-50 translate-x-0' : 'fixed inset-y-0 left-0 z-50 -translate-x-full'}
          transition-transform duration-300 ease-in-out
          md:block
        `}>
          {/* Mobile close button */}
          <div className="md:hidden flex justify-end p-4">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6 flex-shrink-0">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4 z-10" />
              <Input
                type="text"
                placeholder={t('documentation.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchDropdown(e.target.value.trim() !== '');
                }}
                onFocus={() => {
                  if (searchQuery.trim() !== '' && searchResults.length > 0) {
                    setShowSearchDropdown(true);
                  }
                }}
                onBlur={() => {
                  // Delay to allow click events on dropdown items
                  setTimeout(() => setShowSearchDropdown(false), 200);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowSearchDropdown(false);
                    setSearchQuery('');
                  } else if (e.key === 'Enter' && searchResults.length > 0) {
                    // Navigate to first result on Enter
                    navigate(searchResults[0].url);
                    setSearchQuery('');
                    setShowSearchDropdown(false);
                    setIsMobileMenuOpen(false);
                  }
                }}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-white focus:border-cyan-500"
              />
              
              {/* Search Results Dropdown */}
              {showSearchDropdown && searchResults.length > 0 && (
                <div 
                  className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking dropdown
                >
                  <div className="p-2">
                    {searchResults.map((result, index) => (
                      <div
                        key={index}
                        onClick={() => {
                          navigate(result.url);
                          setSearchQuery('');
                          setShowSearchDropdown(false);
                          setIsMobileMenuOpen(false);
                        }}
                        className="p-3 rounded-lg hover:bg-slate-800 cursor-pointer transition-colors border-b border-slate-800 last:border-b-0"
                      >
                        <div className="flex items-start gap-2">
                          {result.item.icon && (
                            <result.item.icon className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-white mb-1">
                              {result.title}
                            </div>
                            <div className="text-xs text-slate-400 line-clamp-2">
                              {result.preview}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {showSearchDropdown && searchQuery.trim() !== '' && searchResults.length === 0 && (
                <div 
                  className="absolute top-full left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 p-4"
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking dropdown
                >
                  <div className="text-sm text-slate-400 text-center">
                    No results found
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 pb-6" style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#3b3a35 #171715'
          }}>
            <nav className="space-y-1">
              {documentationData.sections.map(section => renderSidebarItem(section))}
            </nav>
          </div>
        </div>

        {/* Mobile backdrop */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        
        {/* Main Content */}
        <div className="flex-1 md:ml-0">
          <main className="max-w-4xl mx-auto px-4 md:px-8 py-12">
            {renderContent(currentContent)}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DocumentationPage;